import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { Tenant } from '../services/tenants';
import { tokenManager } from '../services/api';

interface TenantPreviewModalProps {
  visible: boolean;
  onClose: () => void;
  tenant: Tenant | null;
}

interface FloorInfo {
  floor: string;
  rooms: Array<{
    roomNumber: string;
    companyName: string;
    representativeName: string;
    representativeImageUrl?: string;
  }>;
}

const TenantPreviewModal: React.FC<TenantPreviewModalProps> = ({
  visible,
  onClose,
  tenant,
}) => {
  const [floors, setFloors] = useState<FloorInfo[]>([]);
  const [loading, setLoading] = useState(false);

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (phone: string | undefined): string => {
    if (!phone) return '';

    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');

    // 02로 시작하는 서울 지역번호
    if (numbers.startsWith('02')) {
      if (numbers.length === 9) {
        return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
      } else if (numbers.length === 10) {
        return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
      }
    }
    // 휴대폰 번호 (010, 011, 016, 017, 018, 019)
    else if (numbers.startsWith('01')) {
      if (numbers.length === 11) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
      } else if (numbers.length === 10) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
      }
    }
    // 기타 지역번호 (031, 032, 등)
    else if (numbers.length === 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    } else if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }

    return phone;
  };

  // 층수 정보 조회
  useEffect(() => {
    const fetchFloors = async () => {
      if (!tenant?.name || !visible) return;

      // console.log('TenantPreviewModal - fetchFloors called for:', tenant.name);
      // console.log('TenantPreviewModal - tenantUnits exists:', !!tenant.tenantUnits);

      try {
        setLoading(true);
        const token = tokenManager.getAccessToken();
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/cms/tenant-units/by-company?companyName=${encodeURIComponent(tenant.name)}`,
          { headers }
        );
        const data = await response.json();

        // console.log('TenantPreviewModal - API response:', data);

        if (data.success && data.data.floors) {
          // console.log('TenantPreviewModal - floors loaded:', data.data.floors);
          setFloors(data.data.floors);
        } else {
          // console.log('TenantPreviewModal - no floors found');
          setFloors([]);
        }
      } catch (error) {
        // console.error('층수 정보 조회 실패:', error);
        setFloors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFloors();
  }, [visible, tenant?.name]);

  if (!tenant) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1024}
      className="preview-modal"
      closeIcon={<CloseOutlined style={{ color: '#ffffff', fontSize: '20px' }} />}
      styles={{
        body: {
          padding: 0,
          background: '#2a2a2a',
          margin: 0,
          minHeight: '288px',
          maxHeight: '288px'
        },
        content: {
          padding: 0
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)'
        }
      }}
      centered
      destroyOnHidden={true}
      maskClosable={true}
    >
      <div style={{
        display: 'flex',
        width: '1024px',
        height: '288px',
        background: '#0E0E0E',
        position: 'relative',
        boxSizing: 'border-box',
        boxShadow: '0 0 0 0.2px #fff'
      }}>
        {/* 왼쪽 텍스트 영역 (약 411px) */}
        <div style={{
          width: '411px',
          padding: '0px 0px',
          marginRight: '3px', display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          background: '#E7E5DB',
          color: '#000000'
        }}>
          <div
            style={{
              position: 'relative',
              maxWidth: '360px',  
              left: '26px',
            }}
          >
            {/* 입주사명 */}
            <h1 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              margin: '0 0 12px 0',
              // color: '#ffffff',
              lineHeight: '1.2'
            }}>
              {tenant.name}
            </h1>

            {/* 설명 */}
            {tenant.description && (
              <div style={{
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '1.4',
                // color: '#E7E5DB',
                marginBottom: '16px',
                maxHeight: '80px',
                overflow: 'auto',
                whiteSpace: 'pre-line'
              }}>
                {tenant.description.split('\n').map((line, index, array) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* 대표번호 */}
            {tenant.phone && (
              <div style={{ marginBottom: '0px' }}>
                <div style={{
                  fontSize: '8px',
                  // fontWeight: 'bold'
                }}>
                  <span style={{ color: '#7B7872' }}>대표번호</span> {formatPhoneNumber(tenant.phone)}
                </div>
              </div>
            )}

            {/* 층수안내 */}
            {floors.length > 0 && (
              <div style={{ marginBottom: '0px' }}>
                <div style={{
                  fontSize: '8px',
                  // fontWeight: 'bold'
                }}>
                  <span style={{ color: '#7B7872' }}>층수안내</span> {floors.map(f => f.floor.replace('F', '층')).join(', ')}
                </div>
              </div>
            )}
          </div>
          {/* 대표 이미지 - 입주사 관리에서 가져온 이미지 또는 기본 로고 */}
          {(() => {
            // 입주사 관리에서 대표 이미지가 있는 첫 번째 호실 찾기
            let representativeImage: string | undefined;

            // console.log('TenantPreviewModal - Finding representative image');
            // console.log('TenantPreviewModal - tenant.tenantUnits:', tenant.tenantUnits);
            // console.log('TenantPreviewModal - floors:', floors);

            // 1. tenantUnits에서 찾기 (기존 데이터)
            if (tenant.tenantUnits && tenant.tenantUnits.length > 0) {
              representativeImage = tenant.tenantUnits.find(
                unit => unit.representativeImageUrl
              )?.representativeImageUrl;
              // console.log('TenantPreviewModal - Image from tenantUnits:', representativeImage);
            }

            // 2. tenantUnits가 없으면 floors에서 찾기 (입주사명 변경 시)
            if (!representativeImage && floors && floors.length > 0) {
              // console.log('TenantPreviewModal - Searching in floors...');
              for (const floor of floors) {
                const roomWithImage = floor.rooms.find(room => room.representativeImageUrl);
                if (roomWithImage) {
                  representativeImage = roomWithImage.representativeImageUrl;
                  // console.log('TenantPreviewModal - Image from floors:', representativeImage);
                  break;
                }
              }
            }

            // console.log('TenantPreviewModal - Final representative image:', representativeImage);

            return representativeImage ? (
              <div
                style={{
                  position: 'absolute',
                  top: '231px',
                  left: '351px',
                  width: '54px',
                  height: '54px',
                  zIndex: 10,
                  // backgroundColor: '#ffffff',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={representativeImage}
                  alt={`${tenant.name} 로고`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
              </div>
            ) : null;
          })()}
        </div>

        {/* 중앙 이미지 영역 (약 411px) */}
        <div style={{
          width: '411px',
          display: 'flex',
          marginRight: '3px',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          padding: '0px',
          overflow: 'hidden'
        }}>
          {tenant.imageUrl ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%'
            }}>
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  background: '#1a1a1a',
                  position: 'relative'
                }}
              >
                <img
                  src={tenant.imageUrl}
                  alt={tenant.name}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #8B4513; font-size: 14px; font-weight: bold;">이미지 없음</div>';
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              background: '#1a1a1a',
              color: '#666',
              fontSize: '14px'
            }}>
              이미지 없음
            </div>
          )}
        </div>

        {/* 오른쪽 정보 영역 (약 202px) */}
        <div style={{
          width: '202px',
          padding: '0px 0px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: '#ffffff'
        }}>
          <img
            src="/assets/right_panel.png"
            alt="오른쪽 패널 이미지"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default TenantPreviewModal;
